import { useState, useEffect, useRef } from 'react'

import {
  Button,
  CheckBox,
  Col,
  Grid,
  Input,
  Picker,
  Row,
  Span,
  SuccessModal,
  ChipInput,
  UnidadeGestoraFields,
  Line
} from 'components'
import { DraggableDialog } from 'components/DraggableDialog/DraggableDialog'
import { useModal } from 'hooks'
import useAuth from 'hooks/useAuth'
import useLoading from 'hooks/useLoading'
import useToggle from 'hooks/useToggle'
import moment from 'moment'
import Draggable from 'react-draggable'
import { useForm } from 'react-hook-form'
import { ContractServices } from 'services/NGT/Contract'
import { UnidadeGestoraServices } from 'services/Utils/UnidadeGestora'
import { SPACING } from 'utils'

import {
  DialogContentStyled,
  DialogTitleStyled,
  FormDialog
} from './ViewContractModal.style'

interface FormProps {
  showModal: boolean
  toggleModal: () => void
  contractID: number
}

const dateNow = new Date()

export const ViewContractModal: React.FC<FormProps> = ({
  showModal,
  toggleModal,
  contractID
}) => {
  const { setLoading, setMsgErro } = useLoading()
  const { showModal: showAlertModal } = useModal()
  const { user } = useAuth()
  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors }
  } = useForm()

  const creName = watch('creName')
  const setorName = watch('setorName')
  const dtVigInicoReal = watch('dtVigInicoReal')
  const dtVigFimReal = watch('dtVigFimReal')
  const documento = watch('cnpj')
  const [unidadeGestoraId, setUnidadeGestoraId] = useState<number>()
  const [substitutoInputs, setSubstitutoInputs] = useState<any[]>([])
  const [isError, toggleError] = useToggle()
  const [substituteErrorMsg, setSubstituteErrorMsg] = useState<string>('')

  const [flagContratoEncerrado, setFlagContratoEncerrado] =
    useState<boolean>(false)
  const [flagSubcontrato, setFlagSubcontrato] = useState<boolean>(false)
  const [flagSocio, setFlagSocio] = useState<boolean>(false)
  const [flagDenuncia, setFlagDenuncia] = useState<boolean>(false)
  const [flagAlerta, setFlagAlerta] = useState<boolean>(false)
  const [flagRecorrente, setFlagRecorrente] = useState<boolean>(false)
  const [flExclusao, setFlExclusao] = useState<boolean[]>([])

  const handleChangeInput = (value, field) => setValue(field, value)

  const generateInputToken = (length = 10) => {
    let aleatoryString = ''

    const caracteres =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

    for (let i = 0; i < length; i++) {
      aleatoryString += caracteres.charAt(
        Math.floor(Math.random() * caracteres.length)
      )
    }

    return aleatoryString
  }

  const handleChangeOldSubstitutes = (substitutes: any[]) => {
    substitutes.forEach((substitute) => {
      const inputToken = generateInputToken()

      const inputFormValue = {
        email: `emailSubstituto${inputToken}`,
        id: `idSubstituto${inputToken}`,
        matriculaSubstituto: `matriculaSubstituto${inputToken}`,
        ramal: `ramalSubstituto${inputToken}`,
        substituto: `nameSubstituto${inputToken}`
      }

      Object.keys(inputFormValue).forEach((key) => {
        const inputField = inputFormValue[key]

        register(inputField)
      })

      setSubstitutoInputs((prevState) => [...prevState, inputFormValue])

      handleChangeInput(
        substitute.emailContraparteSub.trim(),
        inputFormValue.email
      )
      handleChangeInput(substitute.usuarioId, inputFormValue.id)
      handleChangeInput(
        substitute.matriculaContraparteSub.trim(),
        inputFormValue.matriculaSubstituto
      )
      handleChangeInput(
        substitute.ramalContraparteSub.trim(),
        inputFormValue.ramal
      )
      handleChangeInput(
        substitute.nmContraparteSub.trim(),
        inputFormValue.substituto
      )
    })
  }

  const changeContractValues = (contract: any) => {
    const formatContractValue = contract.vlContratual
      .toLocaleString('pt-BR', { currency: 'BRL', style: 'currency' })
      .toString()
    handleChangeInput(contract.cdContrato, 'cdContrato')
    handleChangeInput(contract.cnpj?.trim(), 'cnpj')
    handleChangeInput(contract.nmFornecedor?.trim(), 'nmFornecedor')
    handleChangeInput(contract.nmEmpresa?.trim(), 'nmEmpresa')
    handleChangeInput(contract.nmCentro?.trim(), 'centroPlantaName')
    handleChangeInput(contract.centroPlantaId, 'centroPlantaId')

    handleChangeInput(contract?.txObjetoContrato?.trim(), 'txObjetoContrato')
    handleChangeInput(contract?.nmCRE?.trim(), 'creName')
    handleChangeInput(contract?.tipoCreId, 'CRE')
    handleChangeInput(contract.txCno?.trim(), 'txCno')
    handleChangeInput(contract.flSocio, 'flSocio')

    setFlagContratoEncerrado(contract.flEncerrado)
    setFlagSubcontrato(contract.flSubcontrato)
    setFlagSocio(contract.flSocio)
    setFlagDenuncia(contract.flDenuncia)
    setFlagAlerta(contract.flAlerta)
    setFlagRecorrente(contract.flRecorrente)
    setUnidadeGestoraId(contract.unidadeGestoraId)

    handleChangeInput(contract.dtVigenciaInicio, 'dtVigenciaInicio')
    handleChangeInput(contract.dtVigenciaFim, 'dtVigenciaFim')
    handleChangeInput(
      contract.dtVigInicoReal === '0001-01-01T00:00:00'
        ? null
        : contract.dtVigInicoReal,
      'dtVigInicoReal'
    )
    handleChangeInput(
      contract.dtVigFimReal === '0001-01-01T00:00:00'
        ? null
        : contract.dtVigFimReal,
      'dtVigFimReal'
    )
    handleChangeInput(formatContractValue, 'vlContratual')
    handleChangeInput(contract.nmSetor, 'setorName')
    handleChangeInput(contract.txDenuncia?.trim(), 'txDenuncia')
    handleChangeInput(contract.txAlerta?.trim(), 'txAlerta')
    handleChangeInput(
      contract.matriculaContraparte?.trim(),
      'matriculaContraparte'
    )
    handleChangeInput(contract.nmContraparte?.trim(), 'nmContraparte')
    handleChangeInput(contract.ramalContraparte?.trim(), 'ramalContraparte')
    handleChangeInput(contract.emailContraparte?.trim(), 'emailContraparte')
    handleChangeInput(contract.matriculaComprador?.trim(), 'matriculaComprador')
    handleChangeInput(contract.nmComprador?.trim(), 'nmComprador')
    handleChangeInput(contract.ramalComprador?.trim(), 'ramalComprador')
    handleChangeInput(contract.emailComprador?.trim(), 'emailComprador')
    handleChangeInput(contract.cdGrupoMercadoria?.trim(), 'cdGrupoMercadoria')
    handleChangeInput(contract.cdHierarquiaProd?.trim(), 'cdHierarquiaProd')
    handleChangeInput(contract.txObservacao?.trim(), 'txObservacao')
    handleChangeInput(contract.nmUnidadeGestora?.trim(), 'nmUnidadeGestora')
    handleChangeInput(contract.nmUnidade?.trim(), 'nmUnidade')
    handleChangeInput(
      contract.txEmails.map((email) => email.txEmail),
      'emailsTrabalhistas'
    )

    contract.txEmails.map(() =>
      setFlExclusao((prevState) => [...prevState, false])
    )
    if (contract?.contraparteSub?.length > 0) {
      handleChangeOldSubstitutes(contract.contraparteSub)
    } else {
      setSubstitutoInputs([
        {
          email: `emailSubstituto0`,
          id: `idSubstituto0`,
          matriculaSubstituto: `matriculaSubstituto0`,
          ramal: `ramalSubstituto0`,
          substituto: `nameSubstituto0`
        }
      ])
    }
  }

  const getUnidadeComprador = (idUnidadeComprador: number) => {
    setLoading('loading')
    UnidadeGestoraServices()
      .getById(idUnidadeComprador)
      .then((response) => {
        if (response?.status == 200) {
          handleChangeInput(response?.data?.numeroUnidade, 'unidadeComprador')
          setLoading('idle')
        } else {
          setLoading('idle')
        }
      })
    setLoading('idle')
  }

  const getContract = () => {
    if (contractID > 0) {
      setLoading('loading')

      ContractServices()
        .GetContractById(contractID)
        .then((response: any) => {
          const { data: contract } = response

          if (response.status === 200) {
            changeContractValues(contract)
            getUnidadeComprador(contract.idUnidadeComprador)
          }

          setLoading('idle')
        })
        .catch((error) => {
          setLoading('error')
          // setMsgErro('Não foi possível obter os dados do contrato.')
          showAlertModal({
            hideButtonCancel: true,
            message: 'Não foi possível obter os dados do contrato',
            txtButtonConfirm: 'OK',
            type: 'error'
          })
        })
    }
  }

  useEffect(() => {
    getContract()
  }, [contractID])

  const draggableReference = useRef<Draggable>(null)

  return (
    <>
      <DraggableDialog
        reference={draggableReference}
        showModal={showModal}
        onClose={toggleModal}
        width={'xl'}
      >
        <FormDialog>
          <Line />
          <DialogTitleStyled>
            Consulta Informações do Contrato
          </DialogTitleStyled>
          <DialogContentStyled>
            <Grid
              gridCols="2fr 2fr 4fr 3fr 3fr 1fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`${SPACING.spacing30} 0 ${SPACING.spacing30} 0`}
            >
              <Input
                control={control}
                errors={errors}
                label="Contrato"
                placeholder="Nº"
                formValue="cdContrato"
                disabled
                min={0}
              />
              <Input
                control={control}
                errors={errors}
                label="CPF/CNPJ/Cód.Fornecedor"
                placeholder="Nº"
                formValue="cnpj"
                mask={
                  documento && documento.length > 9
                    ? ['###.###.###-##', '##.###.###/####-##']
                    : ['#########']
                }
                disabled
              />
              <Input
                control={control}
                errors={errors}
                label="Fornecedor"
                placeholder="Nome do fornecedor"
                formValue="nmFornecedor"
                disabled
              />
              <Input
                control={control}
                errors={errors}
                label="Empresa"
                placeholder="Nome da empresa"
                formValue="nmEmpresa"
                disabled
              />
              <Input
                label="Centro de Custo"
                control={control}
                formValue="centroPlantaName"
                disabled
              />
            </Grid>
            <Grid
              gridCols="4fr 3fr 2fr 1fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`0 0 ${SPACING.spacing30} 0`}
            >
              <Input
                control={control}
                errors={errors}
                label="Objeto contrato"
                formValue="txObjetoContrato"
                disabled
              />
              <Input
                defaultValue={creName}
                label="CRE"
                control={control}
                formValue="creName"
                disabled
              />
              <Input
                control={control}
                errors={errors}
                mask={['##.###.#####/##']}
                label="CNO ( Cadastro Nacional de Obras )"
                placeholder="CNO"
                formValue="txCno"
                disabled
              />
              <CheckBox
                disabled
                checked={flagSocio}
                label="Sócio"
                onClick={(event) => {
                  handleChangeInput(event.target.checked, 'flSocio')
                  setFlagSocio(event.target.checked)
                }}
              />
            </Grid>
            <Grid
              gridCols="1fr 1fr 1fr 1fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`0 0 ${SPACING.spacing30} 0`}
            >
              <Picker
                defaultValue={dateNow}
                label="Início Vigência Contrato"
                formValue="dtVigenciaInicio"
                control={control}
                disabled
              />
              <Picker
                defaultValue={dateNow}
                label="Fim Vigência Contrato"
                formValue="dtVigenciaFim"
                control={control}
                disabled
              />
              <Picker
                defaultValue={dateNow}
                label="Início Vigência Real"
                formValue="dtVigInicoReal"
                maxDate={moment(dtVigFimReal)}
                control={control}
                disabled
              />
              <Picker
                defaultValue={dateNow}
                label="Fim Vigência Real"
                formValue="dtVigFimReal"
                minDate={moment(dtVigInicoReal)}
                control={control}
                disabled
              />
            </Grid>

            <Grid
              gridCols="1fr 1fr 0.5fr 0.5fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`0 0 ${SPACING.spacing30} 0`}
            >
              <Input
                control={control}
                errors={errors}
                label="Valor Contratual"
                placeholder="Valor"
                formValue="vlContratual"
                disabled
                min={0}
              />
              <Input
                defaultValue={setorName}
                label="Setor"
                control={control}
                formValue="setorName"
                disabled
              />
              <Col>
                <Span text="Tipo" />
                <CheckBox
                  disabled
                  checked={flagRecorrente}
                  label="Recorrente"
                  onClick={(event) => {
                    handleChangeInput(event.target.checked, 'flRecorrente')
                    setFlagRecorrente(event.target.checked)
                  }}
                />
              </Col>
              <Col>
                <Span text="Contrato" />
                <CheckBox
                  disabled
                  checked={flagContratoEncerrado}
                  label="Encerrado"
                  onChange={(event) => {
                    handleChangeInput(event.target.checked, 'flEncerrado')
                    setFlagContratoEncerrado(event.target.checked)
                  }}
                />
              </Col>
            </Grid>

            <Span fontSize="1em" text="Notificação" />
            <Grid
              gridCols="0.8fr 0.8fr 0.8fr 2fr 2fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`0 0 ${SPACING.spacing30} 0`}
            >
              <CheckBox
                disabled
                checked={flagDenuncia}
                label="Denúncia"
                onClick={(event) => {
                  handleChangeInput(event.target.checked, 'flDenuncia')
                  setFlagDenuncia(event.target.checked)
                  if (!event.target.checked) {
                    handleChangeInput('', 'txDenuncia')
                  }
                }}
              />
              <CheckBox
                disabled
                checked={flagAlerta}
                label="Alerta"
                onClick={(event) => {
                  handleChangeInput(event.target.checked, 'flAlerta')
                  setFlagAlerta(event.target.checked)
                  if (!event.target.checked) {
                    handleChangeInput('', 'txAlerta')
                  }
                }}
              />
              <CheckBox
                disabled
                checked={flagSubcontrato}
                label="Solicitação Subcontrato"
                onClick={(event) => {
                  handleChangeInput(event.target.checked, 'flSubcontrato')
                  setFlagSubcontrato(event.target.checked)
                }}
              />
              <Input
                maxLength={300}
                control={control}
                errors={errors}
                label="Denúncia"
                formValue="txDenuncia"
                disabled
              />
              <Input
                maxLength={300}
                control={control}
                errors={errors}
                label="Alerta"
                formValue="txAlerta"
                disabled
              />
            </Grid>
            <Grid
              gridCols="1.1fr 2fr 1fr 2.9fr 0.5fr 0.5fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`0 0 ${SPACING.spacing30} 0`}
            >
              <Input
                control={control}
                errors={errors}
                disabled
                label="Matrícula Contraparte"
                placeholder="Nº"
                formValue="matriculaContraparte"
              />
              <Input
                control={control}
                errors={errors}
                disabled
                label="Contraparte"
                formValue="nmContraparte"
                placeholder="Nome do Contraparte"
              />
              <Input
                disabled
                type="number"
                control={control}
                errors={errors}
                label="Ramal"
                formValue="ramalContraparte"
                placeholder="Nº"
              />
              <Input
                disabled
                type="email"
                control={control}
                errors={errors}
                label="E-mail"
                formValue="emailContraparte"
                placeholder="Endereço de E-mail"
              />
            </Grid>
            <>
              {substitutoInputs.length > 0 &&
                substitutoInputs.map((input) => (
                  <>
                    <Grid
                      key={input.id}
                      gridCols="1.1fr 2fr 1fr 2.9fr 0.5fr 0.5fr"
                      gridGap={`0 ${SPACING.spacing20}`}
                      margin={`0 0 ${SPACING.spacing30} 0`}
                    >
                      <Input
                        errors={errors}
                        control={control}
                        label="Matrícula Substituto"
                        placeholder="Nº"
                        formValue={input.matriculaSubstituto}
                        min={0}
                        disabled
                      />
                      <Input
                        control={control}
                        errors={errors}
                        label="Substituto"
                        placeholder="Nome do Substituto"
                        formValue={input.substituto}
                        disabled
                      />
                      <Input
                        type="number"
                        errors={errors}
                        control={control}
                        label="Ramal"
                        placeholder="Nº"
                        formValue={input.ramal}
                        disabled
                      />
                      <Input
                        type="email"
                        control={control}
                        errors={errors}
                        label="E-mail"
                        placeholder="Endereço de E-mail"
                        formValue={input.email}
                        disabled
                      />
                    </Grid>
                  </>
                ))}

              <Grid
                gridCols="1.1fr 2fr 1fr 2.9fr 1.1fr"
                gridGap={`0 ${SPACING.spacing20}`}
                margin={`0 0 ${SPACING.spacing30} 0`}
              >
                <Input
                  control={control}
                  label="Matrícula Comprador"
                  placeholder="Nº"
                  disabled
                  formValue="matriculaComprador"
                  min={0}
                />
                <Input
                  control={control}
                  label="Comprador"
                  placeholder="Nome do Comprador"
                  disabled
                  formValue="nmComprador"
                />
                <Input
                  type="number"
                  control={control}
                  label="Ramal"
                  placeholder="Nº"
                  disabled
                  formValue="ramalComprador"
                  min={0}
                />
                <Input
                  type="email"
                  control={control}
                  label="E-mail"
                  placeholder="Endereço de E-mail"
                  formValue="emailComprador"
                  disabled
                />
                <Input
                  type="number"
                  control={control}
                  label="Unidade do Comprador"
                  placeholder="Unidade do Comprador"
                  formValue="unidadeComprador"
                  min={0}
                  disabled
                />
              </Grid>

              <UnidadeGestoraFields
                gridCols={{
                  Diretoria: 6,
                  DiretoriaExecutiva: 6,
                  Gerencia: 6,
                  GerenciaGeral: 6,
                  Unidade: 12,
                  spacing: 1
                }}
                renderUnidadeSelect
                defaultValue={unidadeGestoraId}
                disableFields={[
                  'DiretoriaExecutiva',
                  'Diretoria',
                  'GerenciaGeral',
                  'Gerencia'
                ]}
                control={control}
                formValue="unidadeGestoraId"
                disabled
              />

              <Grid
                gridCols="1fr 2fr 2fr 1fr 2fr"
                gridGap={`0 ${SPACING.spacing20}`}
                margin={`${SPACING.spacing30} 0 ${SPACING.spacing30} 0`}
              >
                <Input
                  type="number"
                  control={control}
                  errors={errors}
                  label="Cód. Grupo Mercadoria"
                  placeholder="Cód."
                  formValue="cdGrupoMercadoria"
                  disabled
                  min={0}
                />
                {/* <Input
                  control={control}
                  errors={errors}
                  label="Tipo Descrição"
                  placeholder="Tipo"
                  formValue="cdHierarquiaProd"
                  disabled
                /> */}
                <Input
                  control={control}
                  errors={errors}
                  label="Descrição do Grupo Mercadoria"
                  placeholder="Descrição"
                  formValue="descGrupoMercadoria"
                  disabled
                />
                <Input
                  type="number"
                  control={control}
                  errors={errors}
                  label="Cód. Hierarquia Produto"
                  placeholder="Cód."
                  formValue="cdHierarquiaProd"
                  disabled
                  min={0}
                />
                <Input
                  control={control}
                  errors={errors}
                  label="Descrição Hierarquia Produto"
                  placeholder="Descrição"
                  formValue="descHierarquiaProduto"
                  disabled
                />
              </Grid>

              <Grid
                gridCols="8fr "
                gridGap={`0 ${SPACING.spacing20}`}
                margin={`0 0 ${SPACING.spacing30} 0`}
              >
                <ChipInput
                  isDisabled
                  errors={errors}
                  control={control}
                  label="E-mail documentação trabalhista"
                  formValue="emailsTrabalhistas"
                  placeholder=""
                  only="email"
                  disabled
                  flExclusao={flExclusao}
                  setFlExclusao={setFlExclusao}
                />
              </Grid>
              <Grid
                gridCols="8fr"
                gridGap={`0 ${SPACING.spacing20}`}
                margin={`0 0 ${SPACING.spacing5} 0`}
              >
                <Input
                  maxLength={1000}
                  control={control}
                  label="Observação"
                  formValue="txObservacao"
                  disabled
                />
              </Grid>
              <Row justifyContent="flex-end" marginTop={SPACING.spacing20}>
                <Button
                  text="Fechar"
                  marginLeft={SPACING.spacing20}
                  onClick={() => toggleModal()}
                />
              </Row>
            </>
          </DialogContentStyled>
        </FormDialog>
      </DraggableDialog>
      <SuccessModal
        message={substituteErrorMsg}
        open={isError}
        type="error"
        handleConfirm={toggleError}
        txtButtonConfirm="OK"
        hideButtonCancel
      />
    </>
  )
}
